/**
 * `/auth/session-expired` — the global interstitial shown when a session expires
 * at runtime (a bearer-surface `401` that could not be silently refreshed). It
 * informs the operator, counts down 3 seconds, then redirects to centralized
 * login via the shared `redirectToCentralizedLogin` primitive — which stashes the
 * current deep link so the operator lands back where they were after re-auth.
 *
 * The runtime trigger is the in-place render from `AuthGate` (driven by the
 * `AuthContext.sessionExpired` flag set from `signOut({ expired: true })`); the
 * public route registration is only for addressability/testing parity with the
 * other `/auth/*` screens. Because it renders in place (no route change),
 * `useLocation()` still reports the screen the operator was on, so the stashed
 * deep link is correct.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';

const COUNTDOWN_SECONDS = 3;

export function SessionExpired() {
  const location = useLocation();
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Single-fire guard: the countdown reaching 0 and the "Sign in now" button must
  // never both redirect. The ref survives re-renders, so the first caller wins.
  const redirected = useRef(false);

  const goToLogin = useCallback(() => {
    if (redirected.current) return;
    redirected.current = true;
    redirectToCentralizedLogin(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  // Tick the countdown once per second; cleaned up on unmount.
  useEffect(() => {
    const id = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Redirect when the countdown reaches 0 (guarded against double-fire above).
  useEffect(() => {
    if (seconds === 0) goToLogin();
  }, [seconds, goToLogin]);

  return (
    <main
      role="status"
      aria-live="polite"
      style={{ padding: '2rem', maxWidth: '32rem', margin: '0 auto' }}
    >
      <h1 ref={headingRef} tabIndex={-1}>
        Session expired
      </h1>
      <p>Your session has expired for your security. Taking you to sign in…</p>
      <p>Redirecting in {seconds}…</p>
      <button type="button" onClick={goToLogin}>
        Sign in now
      </button>
    </main>
  );
}
